import { supabase } from '@/core/supabaseClient';
import type { ZoneDefinition, ZoneSetKey } from '@/types';

/** Subset of zone keys a user has explicitly overridden — missing keys fall back to defaults. */
export type ZoneOverrides = Partial<Record<ZoneSetKey, ZoneDefinition[]>>;

/**
 * Load the user's saved zone overrides from `user_settings`.
 *
 * Returns:
 *  - `{}` (empty map) when the row exists but the user hasn't customised any set yet, or when the
 *     table is missing (e.g. local Supabase without the latest schema applied — we don't want to
 *     hard-fail the UI just because the migration hasn't run yet).
 *  - `null` ONLY on unexpected programming errors (caller can ignore — defaults are used).
 */
export async function loadUserZones(userId: string): Promise<ZoneOverrides> {
  if (!userId) return {};
  try {
    const { data, error } = await supabase
      .from('user_settings')
      .select('zone_overrides')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      // PGRST205 = relation missing; tolerate it so devs without the migration still get defaults.
      if (error.code === 'PGRST205' || error.code === '42P01') {
        console.warn('[userZonesService] user_settings table missing — using defaults');
        return {};
      }
      console.warn('[userZonesService] loadUserZones error:', error.message);
      return {};
    }

    const raw = (data?.zone_overrides ?? {}) as Record<string, unknown>;
    return sanitizeOverrides(raw);
  } catch (e) {
    console.warn('[userZonesService] loadUserZones threw:', e);
    return {};
  }
}

/**
 * Persist the user's CURRENT zones for ONE (sport, intensity) target. We upsert the JSONB column
 * so other keys (run-pace, run-hr, …) stay untouched. The DB row is created on first save.
 */
export async function saveUserZones(
  userId: string,
  key: ZoneSetKey,
  zones: ZoneDefinition[],
): Promise<{ ok: boolean; error?: string }> {
  if (!userId) return { ok: false, error: 'Missing user id.' };
  if (!Array.isArray(zones) || zones.length === 0) {
    return { ok: false, error: 'Refusing to save empty zone table.' };
  }

  try {
    // Read-modify-write: Supabase JS doesn't support partial JSONB merge in one call, so we
    // pull the current overrides, splice in the new key, and write back. Concurrent edits on
    // multiple devices would race here, but that's a non-issue for solo-user zone editing.
    const existing = await loadUserZones(userId);
    const merged: ZoneOverrides = { ...existing, [key]: zones.map(stripVolatileFields) };

    const ftpWatts = await loadUserFtpWatts(userId);

    const upsertPayload: {
      user_id: string;
      zone_overrides: ZoneOverrides;
      updated_at: string;
      ftp?: number;
    } = {
      user_id: userId,
      zone_overrides: merged,
      updated_at: new Date().toISOString(),
    };
    if (ftpWatts != null) {
      upsertPayload.ftp = ftpWatts;
    }

    const { error } = await supabase.from('user_settings').upsert(upsertPayload, { onConflict: 'user_id' });

    if (error) {
      console.warn('[userZonesService] saveUserZones error:', error.message);
      return { ok: false, error: error.message };
    }
    return { ok: true };
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    console.warn('[userZonesService] saveUserZones threw:', msg);
    return { ok: false, error: msg };
  }
}

const FTP_MIN = 30;
const FTP_MAX = 750;

/**
 * Reads cycling FTP (watts): prefers `profiles.cycling_ftp`, then legacy `profiles.ftp`, then `user_settings.ftp`.
 */
export async function loadUserFtpWatts(userId: string): Promise<number | null> {
  if (!userId) return null;
  try {
    const { data: prof, error: profErr } = await supabase
      .from('profiles')
      .select('cycling_ftp, ftp')
      .eq('id', userId)
      .maybeSingle();

    if (!profErr) {
      const cycle = prof?.cycling_ftp ?? prof?.ftp;
      if (typeof cycle === 'number' && Number.isFinite(cycle) && cycle > 0) {
        return Math.round(Math.max(FTP_MIN, Math.min(FTP_MAX, cycle)));
      }
    } else if (profErr.code !== 'PGRST205' && profErr.code !== '42P01') {
      console.warn('[userZonesService] loadUserFtpWatts profiles error:', profErr.message);
    }

    const { data, error } = await supabase
      .from('user_settings')
      .select('ftp')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      if (error.code === 'PGRST205' || error.code === '42P01') {
        console.warn('[userZonesService] user_settings missing — cannot load FTP');
        return null;
      }
      console.warn('[userZonesService] loadUserFtpWatts error:', error.message);
      return null;
    }

    const n = data?.ftp;
    if (typeof n === 'number' && Number.isFinite(n) && n > 0) {
      return Math.round(Math.max(FTP_MIN, Math.min(FTP_MAX, n)));
    }
    return null;
  } catch (e) {
    console.warn('[userZonesService] loadUserFtpWatts threw:', e);
    return null;
  }
}

/**
 * Persists FTP alongside existing zone overrides (read-modify-write like `saveUserZones`).
 */
export async function saveUserFtpWatts(userId: string, ftpWatts: number): Promise<{ ok: boolean; error?: string }> {
  if (!userId) return { ok: false, error: 'Missing user id.' };
  const ftp = Math.round(Math.max(FTP_MIN, Math.min(FTP_MAX, ftpWatts)));

  try {
    const existingZones = await loadUserZones(userId);
    const ts = new Date().toISOString();
    const { error: profErr } = await supabase.from('profiles').upsert(
      { id: userId, cycling_ftp: ftp, ftp, updated_at: ts },
      { onConflict: 'id' },
    );
    if (profErr && profErr.code !== 'PGRST205' && profErr.code !== '42P01') {
      console.warn('[userZonesService] saveUserFtpWatts profiles error:', profErr.message);
    }

    const { error } = await supabase.from('user_settings').upsert(
      {
        user_id: userId,
        zone_overrides: existingZones,
        ftp,
        updated_at: ts,
      },
      { onConflict: 'user_id' },
    );

    if (error) {
      console.warn('[userZonesService] saveUserFtpWatts error:', error.message);
      return { ok: false, error: error.message };
    }
    return { ok: true };
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    console.warn('[userZonesService] saveUserFtpWatts threw:', msg);
    return { ok: false, error: msg };
  }
}

export async function loadUserRunningFtpWatts(userId: string): Promise<number | null> {
  if (!userId) return null;
  try {
    const { data: prof, error: profErr } = await supabase
      .from('profiles')
      .select('running_ftp')
      .eq('id', userId)
      .maybeSingle();

    if (!profErr) {
      const n = prof?.running_ftp;
      if (typeof n === 'number' && Number.isFinite(n) && n > 0) {
        return Math.round(Math.max(FTP_MIN, Math.min(FTP_MAX, n)));
      }
      return null;
    }
    if (profErr.code === 'PGRST205' || profErr.code === '42P01') {
      return null;
    }
    console.warn('[userZonesService] loadUserRunningFtpWatts profiles error:', profErr.message);
    return null;
  } catch (e) {
    console.warn('[userZonesService] loadUserRunningFtpWatts threw:', e);
    return null;
  }
}

export async function saveUserRunningFtpWatts(userId: string, ftpWatts: number): Promise<{ ok: boolean; error?: string }> {
  if (!userId) return { ok: false, error: 'Missing user id.' };
  const running = Math.round(Math.max(FTP_MIN, Math.min(FTP_MAX, ftpWatts)));

  try {
    const ts = new Date().toISOString();
    const { error: profErr } = await supabase.from('profiles').upsert(
      { id: userId, running_ftp: running, updated_at: ts },
      { onConflict: 'id' },
    );
    if (profErr && profErr.code !== 'PGRST205' && profErr.code !== '42P01') {
      console.warn('[userZonesService] saveUserRunningFtpWatts profiles error:', profErr.message);
      return { ok: false, error: profErr.message };
    }
    return { ok: true };
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    console.warn('[userZonesService] saveUserRunningFtpWatts threw:', msg);
    return { ok: false, error: msg };
  }
}

/** Drop the override for ONE target — used by a hypothetical "Reset to defaults" affordance. */
export async function clearUserZones(
  userId: string,
  key: ZoneSetKey,
): Promise<{ ok: boolean; error?: string }> {
  if (!userId) return { ok: false, error: 'Missing user id.' };
  try {
    const existing = await loadUserZones(userId);
    if (!(key in existing)) return { ok: true };
    const { [key]: _dropped, ...rest } = existing;
    const ftpWatts = await loadUserFtpWatts(userId);

    const upsertPayload: {
      user_id: string;
      zone_overrides: ZoneOverrides;
      updated_at: string;
      ftp?: number;
    } = {
      user_id: userId,
      zone_overrides: rest,
      updated_at: new Date().toISOString(),
    };
    if (ftpWatts != null) {
      upsertPayload.ftp = ftpWatts;
    }

    const { error } = await supabase.from('user_settings').upsert(upsertPayload, { onConflict: 'user_id' });
    if (error) return { ok: false, error: error.message };
    return { ok: true };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}

const VALID_KEYS: readonly ZoneSetKey[] = [
  'bike-power',
  'bike-hr',
  'run-pace',
  'run-power',
  'run-hr',
];

/**
 * Defensive coercion: the JSONB column is user-controlled territory (RLS protects against
 * other users, but a malformed save by an older client still has to be safe to deserialize).
 * We accept only the well-known keys, well-shaped zones, and finite numeric bounds; everything
 * else is silently dropped so the UI seeds with defaults.
 */
function sanitizeOverrides(raw: Record<string, unknown>): ZoneOverrides {
  const out: ZoneOverrides = {};
  for (const key of VALID_KEYS) {
    const value = raw[key];
    if (!Array.isArray(value) || value.length === 0) continue;
    const zones: ZoneDefinition[] = [];
    let allValid = true;
    for (const item of value) {
      if (!item || typeof item !== 'object') {
        allValid = false;
        break;
      }
      const candidate = item as Partial<ZoneDefinition>;
      if (
        typeof candidate.id !== 'string' ||
        typeof candidate.name !== 'string' ||
        typeof candidate.color !== 'string' ||
        !Number.isFinite(candidate.min) ||
        !Number.isFinite(candidate.max)
      ) {
        allValid = false;
        break;
      }
      zones.push({
        id: candidate.id,
        name: candidate.name,
        color: candidate.color,
        min: Number(candidate.min),
        max: Number(candidate.max),
        method: typeof candidate.method === 'string' ? candidate.method : undefined,
      });
    }
    if (allValid) out[key] = zones;
  }
  return out;
}

/** When saving, we strip nothing today but keep the indirection in case fields go transient later. */
function stripVolatileFields(z: ZoneDefinition): ZoneDefinition {
  return { id: z.id, name: z.name, color: z.color, min: z.min, max: z.max, method: z.method };
}
